import { CondicaoPagamentoModoEnum } from '@shared/enums/financas/condicao-pagamento-modo.enum';
import { centavos } from './nfe-pagamento.calculo';
import { proximoDiaUtil } from './nfe-pagamento.dia-util';

/**
 * As contas das parcelas de UM recebimento, como funções puras: quem monta o form
 * chama e grava. Aqui não existe FormGroup, e é o que permite testar as contas de cabeça.
 *
 * Todo dinheiro é feito em CENTAVOS (inteiros), e o resto do arredondamento vai para a ÚLTIMA
 * parcela - é a regra escrita da story.
 */

/** A condição cadastrada, no que interessa para os dias - espelho do `select` do Finanças. */
export interface CondicaoParaParcelas {
    idCondicaoPagamentoModo: number;
    numeroParcelas: number;
    intervaloDias: number;
    diasPorParcela: { ordem: number; dias: number }[];
    taxaJuros: number;
}

/** Uma parcela calculada - o que vira linha da grade. */
export interface ParcelaCalculada {
    numeroParcela: number;
    dias: number;
    dataVencimento: Date;
    valor: number;
}

/** Os dias de vencimento de uma condição CADASTRADA, conforme o modo dela. */
export function diasDaCondicao(condicao: CondicaoParaParcelas): number[] {
    if (condicao.idCondicaoPagamentoModo === CondicaoPagamentoModoEnum.DiasPorParcela) {
        return [...(condicao.diasPorParcela ?? [])].sort((a, b) => a.ordem - b.ordem).map((p) => p.dias);
    }

    const dias: number[] = [];

    for (let i = 1; i <= Math.max(1, condicao.numeroParcelas || 1); i++) {
        dias.push(i * (condicao.intervaloDias || 30));
    }

    return dias;
}

/**
 * Parcelamento do INTERMEDIADOR: a parcela i vence em i × prazo da condição comercial, contado da emissão,
 * pulando para o próximo dia útil quando o intermediador pede. Mesma conta da venda e do Finanças ao lançar o
 * financeiro, para as duplicatas do XML baterem com os títulos.
 */
export function diasDoParcelamentoDoIntermediador(parcelas: number, prazoDias: number, emissao: Date, usarProximoDiaUtil: boolean): number[] {
    const dias: number[] = [];

    for (let i = 1; i <= Math.max(1, parcelas); i++) {
        const vencimento = somarDias(emissao, i * prazoDias);

        dias.push(diasEntre(emissao, usarProximoDiaUtil ? proximoDiaUtil(vencimento) : vencimento));
    }

    return dias;
}

/**
 * Gera as parcelas de um recebimento: o VALOR do recebimento dividido em centavos, resto na
 * última, vencimentos contados a partir da data de emissão da nota.
 *
 * A taxa da condição NÃO entra: Σ parcelas = valor do recebimento, e é o Finanças, ao gerar os
 * títulos, quem aplica juros - aqui a taxa é só a simulação exibida no recebimento.
 */
export function gerarParcelas(valorRecebimento: number, dias: number[], emissao: Date): ParcelaCalculada[] {
    const valores = dividirEmParcelas(valorRecebimento, dias.length);

    return dias.map((d, i) => ({
        numeroParcela: i + 1,
        dias: d,
        dataVencimento: somarDias(emissao, d),
        valor: valores[i],
    }));
}

/**
 * O valor do recebimento dividido em `quantidade` parcelas, em centavos, com o resto na ÚLTIMA. Usado
 * ao gerar e ao mudar o valor do recebimento com parcelas já geradas (dias e vencimentos ficam).
 */
export function dividirEmParcelas(valorRecebimento: number, quantidade: number): number[] {
    if (quantidade <= 0) return [];

    const total = centavos(Math.max(0, valorRecebimento));
    const base = Math.floor(total / quantidade);
    const resto = total - base * quantidade;

    return Array.from({ length: quantidade }, (_, i) => (base + (i === quantidade - 1 ? resto : 0)) / 100);
}

/**
 * Redistribuição automática: editar o valor de uma parcela regera as DEMAIS, dividindo o
 * saldo entre elas.
 *   - intermediária → o saldo vai para as SEGUINTES;
 *   - última → o saldo vai para as ANTERIORES que ainda não foram fixadas pelo usuário.
 * Fixadas (`isValorFixadoUsuario`) ficam como estão; a editada passa a fixada. Centavos, resto na
 * última parcela redistribuída. A soma SEMPRE fecha com o recebimento:
 *   - sem parcela livre para receber o saldo, a própria editada fecha a soma;
 *   - editada acima do que cabe, as livres zeram e a editada fica com o que sobra.
 */
export function redistribuirParcelas<T extends { valor: number; isValorFixadoUsuario: boolean }>(
    parcelas: T[],
    indiceEditado: number,
    totalRecebimento: number,
): T[] {
    const totalC = centavos(totalRecebimento);
    const resultado = parcelas.map((p, i) => (i === indiceEditado ? { ...p, isValorFixadoUsuario: true } : { ...p }));

    if (!resultado.length) return resultado;

    const ehUltima = indiceEditado >= parcelas.length - 1;
    const livres = ehUltima
        ? resultado.map((_, i) => i).filter((i) => i !== indiceEditado && !resultado[i].isValorFixadoUsuario)
        : resultado.map((_, i) => i).filter((i) => i > indiceEditado);
    const alvos = livres.length ? livres : [Math.min(indiceEditado, resultado.length - 1)];

    const fixo = resultado.reduce((soma, p, i) => (alvos.includes(i) ? soma : soma + centavos(p.valor)), 0);
    let saldo = totalC - fixo;

    if (saldo < 0 && !alvos.includes(indiceEditado) && resultado[indiceEditado]) {
        resultado[indiceEditado] = { ...resultado[indiceEditado], valor: (centavos(resultado[indiceEditado].valor) + saldo) / 100 };
        saldo = 0;
    }

    const base = Math.floor(saldo / alvos.length);
    const resto = saldo - base * alvos.length;

    alvos.forEach((indice, posicao) => {
        resultado[indice] = {
            ...resultado[indice],
            valor: (base + (posicao === alvos.length - 1 ? resto : 0)) / 100,
        };
    });

    return resultado;
}

/** Alterar o vencimento no modal recalcula os Dias a partir da emissão. */
export function diasEntre(emissao: Date, vencimento: Date): number {
    const umDia = 24 * 60 * 60 * 1000;
    const a = new Date(emissao.getFullYear(), emissao.getMonth(), emissao.getDate()).getTime();
    const b = new Date(vencimento.getFullYear(), vencimento.getMonth(), vencimento.getDate()).getTime();

    return Math.max(0, Math.round((b - a) / umDia));
}

export function somarDias(data: Date, dias: number): Date {
    const nova = new Date(data.getFullYear(), data.getMonth(), data.getDate());

    nova.setDate(nova.getDate() + dias);

    return nova;
}
